import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  BsShieldLockFill,
  BsArrowCounterclockwise,
  BsTruck,
  BsBuildingCheck,
  BsFileTextFill,
  BsEnvelopeCheckFill,
} from "react-icons/bs";

const RefundReturn = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Refund, Return & Cancellation Protocols | NGO Waters";
  }, []);

  const corePolicies = [
    {
      icon: <BsShieldLockFill className="fs-5 text-danger" />,
      title: "Seal & Integrity Violations",
      text: "Packaged mineral water products are non-returnable once custody transitions past delivery checkpoints, unless structural container degradation, batch leakage, or primary cap seal compromises are verified upon receipt.",
    },
    {
      icon: (
        <BsTruck
          className="fs-5"
          style={{ color: "var(--brand-mineral-aqua)" }}
        />
      ),
      title: "Bulk Transit Logistics",
      text: "For high-capacity enterprise pipeline orders or commercial distribution batches, any discrepancies in volume metrics or mechanical damage during transit must be logged directly on the physical Delivery Challan.",
    },
    {
      icon: <BsArrowCounterclockwise className="fs-5 text-success" />,
      title: "Audit & Settlement Cycle",
      text: "Once a quality constraint anomaly is officially approved by our compliance unit, systemic reversals are cleared inside 5 to 7 operational banking days directly via the primary corporate settlement channel.",
    },
  ];

  const springTransition = { type: "spring", stiffness: 50, damping: 16 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Ambient Decorative Blobs */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "600px",
          height: "600px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-250px",
          right: "-100px",
          filter: "blur(140px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "10%",
          left: "-100px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* Production Grade SEO / GEO / AEO Schema Injection */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "NGO Waters Refund and Return Policies",
          description:
            "Official FMCG regulatory return, refund, and batch cancellation frameworks for NGO Waters under Ponia Food And Beverage Private Limited.",
          publisher: {
            "@type": "Organization",
            name: "PONIA FOOD AND BEVERAGE PRIVATE LIMITED",
            duns: "CIN: U11043UP2024PTC213517",
            location: {
              "@type": "PostalAddress",
              addressLocality: "Aligarh",
              addressRegion: "Uttar Pradesh",
              postalCode: "202123",
              addressCountry: "IN",
            },
          },
        })}
      </script>

      {/* ================= LEGAL HERO HEADER ================= */}
      <section className="container py-4 position-relative z-1 mb-5">
        <div className="row justify-content-center text-center">
          <div className="col-lg-9 col-xl-8">
            <span
              className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
              style={{
                backgroundColor: "rgba(26, 60, 107, 0.06)",
                color: "var(--brand-deep-blue)",
                fontSize: "0.72rem",
                borderRadius: "6px",
              }}
            >
              Commercial Compliance Documentation
            </span>
            <h1
              className="display-5 fw-bold mb-3"
              style={{
                color: "var(--brand-deep-blue)",
                letterSpacing: "-1.5px",
              }}
            >
              Settlement & Return Framework
            </h1>
            <p
              className="text-secondary small mx-auto mb-0"
              style={{ maxWidth: "580px", lineHeight: "1.7" }}
            >
              Official logistical provisions governing cancellation criteria,
              batch replacements, and financial reversal matrices for
              high-volume trade distributions.
            </p>
          </div>
        </div>
      </section>

      {/* ================= POLICY SPECIFICATION GRID ================= */}
      <section className="container mb-5 position-relative z-1">
        <div className="row g-4 justify-content-center">
          {corePolicies.map((policy, index) => (
            <div className="col-md-4" key={index}>
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{
                  y: -5,
                  boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
                }}
                className="card border-0 h-100 bg-white p-4"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.03)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center mb-3"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.012)",
                    width: "42px",
                    height: "42px",
                  }}
                >
                  {policy.icon}
                </div>
                <h5
                  className="fw-bold text-dark mb-3"
                  style={{ fontSize: "1.05rem", lineHeight: "1.3" }}
                >
                  {policy.title}
                </h5>
                <p
                  className="text-muted small mb-0"
                  style={{ lineHeight: "1.65" }}
                >
                  {policy.text}
                </p>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= EXPANDED DETAILED STATEMENTS ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <div
              className="p-4 p-md-5 bg-white border"
              style={{ borderRadius: "24px", borderColor: "rgba(0,0,0,0.03)" }}
            >
              <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom border-light">
                <BsFileTextFill className="text-muted fs-5" />
                <h4
                  className="fw-bold mb-0 fs-5"
                  style={{ color: "var(--brand-deep-blue)" }}
                >
                  Standard Operating Guidelines
                </h4>
              </div>

              <div
                className="d-flex flex-column gap-4 small text-secondary"
                style={{ lineHeight: "1.75" }}
              >
                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    1. Batch Reclamation Protocol
                  </h6>
                  <p className="mb-0">
                    As an enterprise specializing in scalable hydration
                    allocation systems, individual beverage consumer nodes fall
                    beneath explicit sanitation mandates. Commercial or
                    institutional purchases verified as contaminated, or
                    demonstrating clear variance from standard{" "}
                    <strong>IS 14543 physical water parameters</strong>, will
                    immediately spark a comprehensive lot-reclamation sequence
                    at zero incremental liability to the client.
                  </p>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    2. Order Cancellations & Loading Locks
                  </h6>
                  <p className="mb-0">
                    Schedules for automated freight assembly lines lock inside
                    our local logistical hubs systematically. Requests to
                    suspend or cancel bulk fleet deliveries must be documented
                    at least 24 hours prior to scheduled departure.
                    Cancellations requested after trucks are actively loaded
                    attract a standard recovery surcharge to offset initial
                    regional logistics variables.
                  </p>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    3. Corporate Accountability Foundations
                  </h6>
                  <p className="mb-0">
                    All financial operations, compliance verification tracks,
                    and supply channels are legally backed by the executive
                    corporate grid of{" "}
                    <strong>PONIA FOOD AND BEVERAGE PRIVATE LIMITED</strong>{" "}
                    (CIN: U11043UP2024PTC213517), established in Aligarh, Uttar
                    Pradesh. Transparency remains a baseline condition; hence,
                    50% of the proceeds generated from standard commercial
                    frameworks remain tied cleanly to public infrastructure
                    utility setups.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= ACTION / ESCALATION CARD ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <div
              className="bg-white border p-4 p-md-5 text-center text-md-start"
              style={{
                borderRadius: "24px",
                borderColor: "rgba(0,0,0,0.04)",
                borderLeft: "5px solid var(--brand-copper)",
              }}
            >
              <div className="row align-items-center g-4">
                <div className="col-md-8">
                  <div className="d-flex align-items-center gap-2 mb-2 justify-content-center justify-content-md-start">
                    <BsBuildingCheck className="text-dark fs-5" />
                    <h5
                      className="fw-bold mb-0 text-dark"
                      style={{ fontSize: "1.1rem" }}
                    >
                      Escalate to Quality Assurance Bureau
                    </h5>
                  </div>
                  <p
                    className="text-muted small mb-0"
                    style={{ lineHeight: "1.6" }}
                  >
                    To flag any anomalies in a batch or request physical
                    validation checks on seals, immediately connect with our
                    legal compliance desk.
                  </p>
                </div>
                <div className="col-md-4 text-md-end">
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={springTransition}
                    href="mailto:support@ngowaters.com"
                    className="btn px-4 py-2.5 text-white fw-bold border-0 d-inline-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "var(--brand-deep-blue)",
                      borderRadius: "10px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <BsEnvelopeCheckFill /> support@ngowaters.com
                  </motion.a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default RefundReturn;
